import useSWR from "swr";
import { fetcher, api } from "@/lib/api";
import { PageHeader } from "@/components/Heading";
import { formatIndianCurrency, formatIndianGrouping, formatPct } from "@/lib/format";
import { useState } from "react";
import { Package, X, Tag, Layers, Store as StoreIcon, Sparkles, ArrowRight, Boxes } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function PriceCard({ label, value, tone = "default" }) {
  const cls = tone === "accent" ? "border-primary text-primary" : tone === "warn" ? "border-warning text-warning" : "";
  return (
    <div className={`hairline px-4 py-3 ${cls}`}>
      <div className="label-caps">{label}</div>
      <div className="font-mono text-lg mt-1">{formatIndianCurrency(value)}</div>
    </div>
  );
}

function BarList({ items }) {
  const max = items[0]?.qty || 1;
  return items.map((c) => (
    <div key={c.label} className="py-1.5">
      <div className="flex items-baseline justify-between text-xs mb-1">
        <span>{c.label}</span>
        <span className="font-mono text-muted-foreground">{formatIndianGrouping(c.qty)}</span>
      </div>
      <div className="h-1 bg-secondary">
        <div className="h-full bg-primary" style={{ width: `${(c.qty / max) * 100}%` }} />
      </div>
    </div>
  ));
}

function ProductDrawer({ item, onClose }) {
  const [tab, setTab] = useState("overview");
  const { data: detail } = useSWR(`/stock/product-detail?product_name=${encodeURIComponent(item.name)}`, fetcher);
  const p = detail?.product || item;
  const tabs = ["overview", "colors", "sizes", "vendors", "recovery"];

  const partnerDailyRate = (item.bundle_daily_rate || 1) - (item.daily_sales_rate || 0.1) || 8.2;
  const effectiveMrp = item.avg_mrp || item.avg_purc_rate * 1.5;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex justify-end" data-testid="product-drawer">
      <div className="w-full md:w-[680px] bg-background hairline-l h-full overflow-y-auto">
        <div className="hairline-b sticky top-0 bg-background z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 hairline grid place-items-center"><Package className="w-4 h-4" /></div>
              <div className="min-w-0">
                <div className="font-display text-xl leading-tight truncate" title={item.name}>{item.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{item.department} · {item.vendor_name}</div>
              </div>
            </div>
            <button onClick={onClose} data-testid="drawer-close" className="w-9 h-9 hairline grid place-items-center hover:bg-secondary">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-px bg-border">
            <div className="bg-background px-4 py-3">
              <div className="label-caps">Units</div>
              <div className="font-mono">{formatIndianGrouping(item.closing_qty)}</div>
            </div>
            <div className="bg-background px-4 py-3">
              <div className="label-caps">Value</div>
              <div className="font-mono">{formatIndianCurrency(item.closing_value)}</div>
            </div>
            <div className="bg-background px-4 py-3">
              <div className="label-caps">Daily rate</div>
              <div className="font-mono">{item.daily_sales_rate || 0.1}</div>
            </div>
            <div className="bg-background px-4 py-3">
              <div className="label-caps">DoI</div>
              <div className="font-mono text-warning">{item.doi || "—"}d</div>
            </div>
          </div>
          <div className="flex overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t}
                data-testid={`drawer-tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-4 py-3 text-xs uppercase tracking-widest font-mono ${
                  tab === t ? "border-b-2 border-primary text-foreground" : "text-muted-foreground"
                }`}
              >{t}</button>
            ))}
          </div>
        </div>

        <div className="px-6 py-6">
          {tab === "overview" && (
            <div>
              <div className="grid grid-cols-2 gap-px bg-border">
                <div className="bg-background p-4">
                  <div className="label-caps">Fabric</div><div className="font-display text-base">{p.fabric}</div>
                </div>
                <div className="bg-background p-4">
                  <div className="label-caps">Style</div><div className="font-display text-base">{p.fashion_style}</div>
                </div>
                <div className="bg-background p-4">
                  <div className="label-caps">Pattern</div><div className="font-display text-base">{p.pattern}</div>
                </div>
                <div className="bg-background p-4">
                  <div className="label-caps">Color</div><div className="font-display text-base">{p.color}</div>
                </div>
              </div>
              <div className="mt-6 hairline p-4">
                <div className="label-caps mb-2">Sell-through</div>
                <div className="font-mono text-2xl">{formatPct(p.sell_through_pct)}</div>
                <div className="h-1.5 mt-2 bg-secondary">
                  <div className="h-full bg-primary" style={{ width: `${Math.min(p.sell_through_pct || 0, 100)}%` }} />
                </div>
              </div>
            </div>
          )}
          {tab === "colors" && (
            <div className="hairline p-4">
              <div className="label-caps mb-3">Top 15 colors</div>
              <BarList items={detail?.colors || []} />
            </div>
          )}
          {tab === "sizes" && (
            <div className="hairline p-4">
              <div className="label-caps mb-3">Top 10 sizes</div>
              <BarList items={detail?.sizes || []} />
            </div>
          )}
          {tab === "vendors" && (
            <div className="hairline p-4">
              <div className="label-caps mb-3">Top 10 vendors</div>
              <BarList items={detail?.vendors || []} />
            </div>
          )}
          {tab === "recovery" && (
            <div className="space-y-6">
              <div className="hairline p-4 border-warning">
                <div className="flex items-start gap-3">
                  <Tag className="w-5 h-5 text-warning mt-0.5" />
                  <div>
                    <div className="font-display text-lg">Dead stock alert</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      At current rate, this SKU will take ~{Math.round(item.closing_qty / Math.max(item.daily_sales_rate, 0.1))} days to clear. Discount or bundle.
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-px bg-border">
                <PriceCard label="MRP" value={effectiveMrp} />
                <PriceCard label="Cost" value={item.avg_purc_rate} />
                <PriceCard label="Sell at" value={item.suggested_price} tone="accent" />
              </div>
              <div className="hairline p-4">
                <div className="font-display text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Markdown strategy</div>
                <div className="text-sm mt-2 text-muted-foreground">Suggested markdown: <span className="text-primary font-mono">{item.markdown_pct}%</span> off MRP. Clears in ~{Math.round(item.closing_qty / Math.max(item.daily_sales_rate * 2, 0.5))} days at 2× lift.</div>
              </div>
              <div className="hairline p-5 border-primary/40 bg-secondary/30">
                <div className="flex items-center gap-2 mb-4">
                  <Boxes className="w-4 h-4 text-primary" />
                  <span className="label-caps text-primary">Bundle strategy · killer recovery play</span>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                  <div className="hairline p-3">
                    <div className="text-[11px] text-muted-foreground">DEAD</div>
                    <div className="font-display text-sm leading-tight">{item.name}</div>
                    <div className="font-mono text-xs mt-2">{formatIndianCurrency(item.suggested_price)}</div>
                  </div>
                  <div className="text-2xl text-muted-foreground">+</div>
                  <div className="hairline p-3 border-primary/40">
                    <div className="text-[11px] text-primary">FAST · {partnerDailyRate.toFixed(1)}/day</div>
                    <div className="font-display text-sm leading-tight">{item.bundle_partner}</div>
                    <div className="font-mono text-xs mt-2">{formatIndianCurrency((item.bundle_price || 0) - item.suggested_price)}</div>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between hairline-t pt-4">
                  <div>
                    <div className="label-caps">Bundle price</div>
                    <div className="font-mono text-xl">{formatIndianCurrency(item.bundle_price)}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <div className="text-right">
                    <div className="label-caps">Clears in</div>
                    <div className="font-mono text-xl text-positive">{Math.round(item.bundle_doi)}d</div>
                    <div className="text-[11px] text-positive">≈{Math.round((item.closing_qty / Math.max(item.daily_sales_rate, 0.1)) / Math.max(item.bundle_doi, 1))}× faster</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeadStock() {
  const [tier, setTier] = useState("dead");
  const { data } = useSWR(`/stock/dead-stock-recovery?tier=${tier}`, fetcher);
  const items = data?.items || [];
  const [open, setOpen] = useState(null);

  return (
    <div>
      <PageHeader
        eyebrow="Stock Intelligence · Recovery"
        title="The parked-cash list."
        lede="Sortable inventory of dead and at-risk SKUs with markdown + bundle plays calibrated to days-of-inventory."
        right={
          <div className="hairline flex">
            {[["dead", "Dead"], ["at_risk", "At risk"]].map(([k, l], i) => (
              <button
                key={k}
                data-testid={`tier-${k}`}
                onClick={() => setTier(k)}
                className={`px-3 h-9 font-mono text-xs tracking-wider ${i > 0 ? "border-l border-border" : ""} ${
                  tier === k ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"
                }`}
              >{l}</button>
            ))}
          </div>
        }
      />

      <div className="hidden md:block hairline">
        <table className="w-full table-dense">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Value</th>
              <th className="text-right">Age</th>
              <th className="text-right">Rate/day</th>
              <th className="text-right">Markdown</th>
              <th className="text-right">Sell at</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.product_id}
                data-testid="dead-stock-row"
                onClick={() => setOpen(it)}
                className="cursor-pointer"
              >
                <td className="max-w-[280px]">
                  <div className="font-display text-sm truncate" title={it.name}>{it.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{it.department} · {it.vendor_name}</div>
                </td>
                <td className="text-right">{formatIndianGrouping(it.closing_qty)}</td>
                <td className="text-right">{formatIndianCurrency(it.closing_value)}</td>
                <td className="text-right">{it.avg_age_days}d</td>
                <td className="text-right text-muted-foreground">{it.daily_sales_rate}</td>
                <td className="text-right text-warning">{it.markdown_pct}%</td>
                <td className="text-right text-primary">{formatIndianCurrency(it.suggested_price)}</td>
                <td className="text-right text-muted-foreground">→</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {items.map((it) => (
          <button
            key={it.product_id}
            onClick={() => setOpen(it)}
            data-testid="dead-stock-card-mobile"
            className="w-full text-left hairline p-4"
          >
            <div className="font-display text-base">{it.name}</div>
            <div className="text-[11px] text-muted-foreground">{it.department}</div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs font-mono">
              <div><div className="label-caps">Qty</div>{formatIndianGrouping(it.closing_qty)}</div>
              <div><div className="label-caps">Value</div>{formatIndianCurrency(it.closing_value)}</div>
              <div><div className="label-caps">Mark</div><span className="text-warning">{it.markdown_pct}%</span></div>
            </div>
          </button>
        ))}
      </div>

      {open && <ProductDrawer item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
